import React from 'react';
import type {
  FormElement,
  FormData,
  ThemeConfig,
  ComponentMap,
} from '../types';
import type { ConditionalLogicEvaluator } from '../conditional-logic/evaluator';
import { getFieldComponent } from './fieldComponentRegistry';

/**
 * Props for ElementRenderer
 */
interface ElementRendererProps {
  element: FormElement;
  formData: FormData;
  errors: Record<string, string[]>;
  touched: Set<string>;
  onChange: (fieldId: string, value: any) => void;
  onBlur: (fieldId: string) => void;
  disabled?: boolean;
  theme?: ThemeConfig;
  customComponents?: ComponentMap;
  logicEvaluator: ConditionalLogicEvaluator;
}

/**
 * ElementRenderer Component
 *
 * Recursively renders form elements based on their type.
 * Handles conditional logic visibility.
 */
export const ElementRenderer: React.FC<ElementRendererProps> = ({
  element,
  formData,
  errors,
  touched,
  onChange,
  onBlur,
  disabled,
  theme,
  customComponents,
  logicEvaluator,
}) => {
  // Check if element is explicitly hidden
  if (element.properties?.isHidden) {
    return null;
  }

  // Check conditional logic visibility
  const state = logicEvaluator.getElementState(element.id);

  // Don't render if hidden by conditional logic
  if (!state.visible) {
    return null;
  }

  // Apply custom styles
  const applyCustomStyles = (): React.CSSProperties => {
    const styles: React.CSSProperties = {};

    if (element.properties?.customStyles) {
      element.properties.customStyles.forEach(({ property, value }) => {
        const camelProp = property.replace(/-([a-z])/g, (_, letter) => letter.toUpperCase());
        (styles as any)[camelProp] = value;
      });
    }

    // Apply highlight if needed
    if (state.highlighted) {
      styles.backgroundColor = theme?.colors?.success || '#fff3cd';
      styles.border = `2px solid ${theme?.colors?.success || '#ffc107'}`;
    }

    return styles;
  };

  const elementStyles = applyCustomStyles();
  const elementClassName = element.properties?.className || '';

  // Render container elements (form, chapter, section, group)
  if (['form', 'chapter', 'section', 'document-section', 'group'].includes(element.type)) {
    const containerClass = `mediform-${element.type} ${elementClassName}`;
    const headerStyles = element.properties?.customHeaderStyles
      ? element.properties.customHeaderStyles.reduce((acc, { property, value }) => {
          const camelProp = property.replace(/-([a-z])/g, (_, letter) => letter.toUpperCase());
          (acc as any)[camelProp] = value;
          return acc;
        }, {} as React.CSSProperties)
      : {};

    const contentStyles = element.properties?.customContentStyles
      ? element.properties.customContentStyles.reduce((acc, { property, value }) => {
          const camelProp = property.replace(/-([a-z])/g, (_, letter) => letter.toUpperCase());
          (acc as any)[camelProp] = value;
          return acc;
        }, {} as React.CSSProperties)
      : {};

    return (
      <div className={containerClass} style={elementStyles}>
        {/* Render heading if present and not hidden */}
        {element.properties?.heading && !element.properties.hideHeading && (
          <div className={`mediform-${element.type}-header`} style={headerStyles}>
            <h2 className="mediform-heading">{element.properties.heading}</h2>
          </div>
        )}

        {/* Render children */}
        <div className={`mediform-${element.type}-content`} style={contentStyles}>
          {element.children?.map(child => (
            <ElementRenderer
              key={child.id}
              element={child}
              formData={formData}
              errors={errors}
              touched={touched}
              onChange={onChange}
              onBlur={onBlur}
              disabled={disabled}
              theme={theme}
              customComponents={customComponents}
              logicEvaluator={logicEvaluator}
            />
          ))}
        </div>
      </div>
    );
  }

  // Render label element (with calculated value support)
  if (element.type === 'label') {
    const displayLogic = element.properties?.displayLogic;
    let text = element.properties?.label || element.properties?.content || element.properties?.heading || '';

    // Handle calculated/interpretation labels
    if ((element.properties?.isCalculated || element.properties?.isInterpretation) && displayLogic && displayLogic.outputVariable) {
      const calculatedValues = logicEvaluator.getCalculatedValues();
      const variableName = displayLogic.outputVariable;
      const calculatedValue = calculatedValues[variableName];

      // Check if all dependencies are met
      const allDependenciesMet = (displayLogic.dependencies || []).every(depId =>
        (formData[depId] !== undefined && formData[depId] !== '') ||
        (calculatedValues[depId] !== undefined && calculatedValues[depId] !== '')
      );

      let displayValue: string;
      if (!allDependenciesMet) {
        displayValue = '';
      } else if (calculatedValue === null || calculatedValue === undefined) {
        displayValue = 'Error';
      } else {
        displayValue = String(calculatedValue);
      }

      // Replace placeholder or use value directly
      const placeholder = `{{${variableName}}}`;
      if (text.includes(placeholder)) {
        text = text.replace(new RegExp(placeholder.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&'), 'g'), displayValue);
      } else {
        text = displayValue;
      }
    }

    return (
      <div className={`mediform-label ${elementClassName}`} style={elementStyles}>
        {text}
      </div>
    );
  }

  // Get component (custom or default)
  const FieldComponent = customComponents?.[element.type] || getFieldComponent(element.type);

  const fieldProps = {
    element,
    value: formData[element.id],
    onChange: (value: any) => onChange(element.id, value),
    onBlur: () => onBlur(element.id),
    error: touched.has(element.id) ? errors[element.id]?.[0] : undefined,
    disabled,
    theme,
    required: state.required,
  };

  if (FieldComponent) {
    return (
      <div className={`mediform-field ${elementClassName}`} style={elementStyles}>
        <FieldComponent {...fieldProps} />
      </div>
    );
  }

  // Fallback for unknown field types
  return (
    <div className={`mediform-field mediform-field-unknown ${elementClassName}`} style={elementStyles}>
      <div className="mediform-field-error">
        Unknown field type: {element.type}
      </div>
    </div>
  );
};

export default ElementRenderer;
